// The end-of-game recap table: one row per player, one column per stat.
// Shared by both game-over modals (the bankruptcy win and the final-standings one).
import { useMemo } from "react"
import classNames from "classnames"
import { TrophyIcon } from "@heroicons/react/24/solid"
import { allPieces } from "@/utils/data"

// Everything the recap table shows per player, all derived from the game's
// history items (the same log the in-game history panel reads).
export interface EndGameStats {
  propertiesBought: number
  housesBought: number
  housesSold: number
  rentGained: number
  topRent: number
  rentPaid: number
  cardsPlayed: number
  trades: number
  stockNet: number
}

const emptyStats = (): EndGameStats => ({
  propertiesBought: 0,
  housesBought: 0,
  housesSold: 0,
  rentGained: 0,
  topRent: 0,
  rentPaid: 0,
  cardsPlayed: 0,
  trades: 0,
  stockNet: 0,
})

const STAT_COLUMNS: {
  key: keyof EndGameStats
  label: string
  money?: boolean
  /** Renders with a +/- sign and profit/loss coloring. */
  signed?: boolean
}[] = [
  { key: "propertiesBought", label: "Properties Bought" },
  { key: "housesBought", label: "Houses Bought" },
  { key: "housesSold", label: "Houses Sold" },
  { key: "rentGained", label: "Rent Earned", money: true },
  { key: "topRent", label: "Top Rent Earned", money: true },
  { key: "rentPaid", label: "Rent Paid", money: true },
  { key: "cardsPlayed", label: "Cards Played" },
  { key: "trades", label: "Trades" },
  { key: "stockNet", label: "Stock Net", money: true, signed: true },
]

// Walks the game log once and tallies every player's numbers. Trades come from
// the situation's completed-trade list rather than the log: the "trade-successful"
// history item is declared but never emitted — accepting a trade appends to
// situation.completedTrades instead (see socketsEvents/trade.ts).
export const buildStats = (
  players: IPlayer[] | undefined,
  historyItems: IHistoryItem[] | undefined,
  completedTrades?: ITrade[]
): Record<number, EndGameStats> => {
  const stats: Record<number, EndGameStats> = {}
  ;(players || []).forEach((player) => {
    stats[player.order] = emptyStats()
  })
  const statsFor = (order?: PlayerOrder) =>
    order === undefined ? undefined : (stats[order] ||= emptyStats())

  ;(historyItems || []).forEach((item) => {
    switch (item.type) {
      // A property leaves the bank either by a direct buy or by winning its auction.
      case "private-buy":
      case "auction-won": {
        const s = statsFor(item.playerOrder)
        if (s) s.propertiesBought++
        break
      }
      case "house-bought": {
        const s = statsFor(item.playerOrder)
        if (s) s.housesBought += item.count
        break
      }
      case "house-sold": {
        const s = statsFor(item.playerOrder)
        if (s) s.housesSold += item.count
        break
      }
      case "rent-paid": {
        const payer = statsFor(item.playerOrder)
        if (payer) payer.rentPaid += item.rent
        // Legacy log items have no ownerOrder — those only count as rent paid.
        const owner = statsFor(item.ownerOrder)
        if (owner) {
          owner.rentGained += item.rent
          owner.topRent = Math.max(owner.topRent, item.rent)
        }
        break
      }
      // Stock net is realized cash only: what came back out minus what went in.
      // Stock still sitting in the exchange at game end reads as a loss here —
      // it's counted as net worth on the final-standings cards instead.
      case "stock-invested": {
        const s = statsFor(item.playerOrder)
        if (s) s.stockNet -= item.amount
        break
      }
      case "stock-retrieved": {
        const s = statsFor(item.playerOrder)
        if (s) s.stockNet += item.amount
        break
      }
      case "card-played": {
        const s = statsFor(item.playerOrder)
        if (s) s.cardsPlayed++
        break
      }
    }
  })

  // Both sides of a completed trade get credit for it.
  ;(completedTrades || []).forEach((trade) => {
    const offering = statsFor(trade.offeringPlayerOrder)
    if (offering) offering.trades++
    const offered = statsFor(trade.offeredPlayerOrder)
    if (offered) offered.trades++
  })

  return stats
}

interface GameStatsTableProps {
  /** Players in the order they should be listed. */
  players: IPlayer[]
  /** The game's users, for resolving usernames (myGame.players). */
  users?: IUser[]
  /** Highlighted row, if the game has a winner. */
  winnerOrder?: number | null
  historyItems?: IHistoryItem[]
  completedTrades?: ITrade[]
  /** Tighter rows and smaller numbers, for screens that also show other panels. */
  dense?: boolean
  className?: string
}

const GameStatsTable: React.FC<GameStatsTableProps> = ({
  players,
  users,
  winnerOrder,
  historyItems,
  completedTrades,
  dense = false,
  className,
}) => {
  const stats = useMemo(
    () => buildStats(players, historyItems, completedTrades),
    [players, historyItems, completedTrades]
  )

  return (
    <div
      className={classNames(
        "overflow-auto rounded-2xl border border-gray-700/60 bg-black/25",
        className
      )}
    >
      <table className="w-full border-collapse text-center font-poppins">
        <thead>
          <tr className="bg-white/5 text-[0.65rem] uppercase tracking-wider text-white/40">
            <th
              className={classNames(
                "px-4 text-left font-medium",
                dense ? "py-1.5" : "py-3"
              )}
            >
              Player
            </th>
            {STAT_COLUMNS.map((column) => (
              <th
                key={column.key}
                className={classNames(
                  "px-3 font-medium",
                  dense ? "py-1.5" : "py-3"
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {players.map((player) => {
            const user = users?.find((p) => p.email === player.id)
            const piece = allPieces.find((p) => p.id === player.pieceId)
            const isWinner = player.order === winnerOrder
            const playerStats = stats[player.order] || emptyStats()

            return (
              <tr
                key={player.order}
                className={classNames("border-t border-white/5", {
                  "bg-amber-400/10": isWinner,
                })}
              >
                <td
                  className={classNames(
                    "px-4 text-left",
                    dense ? "py-1.5" : "py-3"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {piece?.imgSrc && (
                      <img
                        src={piece.imgSrc}
                        alt=""
                        className={classNames(
                          "shrink-0 object-contain",
                          dense ? "h-6 w-6" : "h-7 w-7",
                          { grayscale: player.isBankrupt }
                        )}
                      />
                    )}
                    <span
                      className={classNames("truncate text-sm", {
                        "font-semibold text-amber-200": isWinner,
                        "text-white/80": !isWinner && !player.isBankrupt,
                        "text-white/40": player.isBankrupt,
                      })}
                    >
                      {user?.username || `Player ${player.order}`}
                    </span>
                    {isWinner && (
                      <TrophyIcon className="h-4 w-4 shrink-0 text-amber-300" />
                    )}
                    {player.isBankrupt && (
                      <span className="shrink-0 rounded-md bg-red-500/15 px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wide text-red-300/80">
                        Bankrupt
                      </span>
                    )}
                  </div>
                </td>

                {STAT_COLUMNS.map((column) => {
                  const value = playerStats[column.key]
                  const isProfit = !!column.signed && value > 0
                  const isLoss = !!column.signed && value < 0
                  const sign = value < 0 ? "-" : isProfit ? "+" : ""

                  return (
                    <td
                      key={column.key}
                      className={classNames(
                        "px-3 font-semibold tabular-nums",
                        dense ? "py-1.5 text-base" : "py-3 text-lg",
                        {
                          "text-emerald-300": isProfit,
                          "text-red-300": isLoss,
                          "text-amber-100": isWinner && !isProfit && !isLoss,
                          "text-white/80": !isWinner && !isProfit && !isLoss,
                          "opacity-30": !value,
                        }
                      )}
                    >
                      {column.money
                        ? `${sign}M${Math.abs(value).toLocaleString()}`
                        : value.toLocaleString()}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default GameStatsTable
